import { useRecordingContext } from '@/contexts/RecordingContext';
import { RecordingStateSelector } from '@/store/selectors';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

interface AudioWaveformProps {
  className?: string;
  height?: number;
  barWidth?: number;
  barGap?: number;
  barColor?: string;
  backgroundColor?: string;
  silenceColor?: string;
}

const AudioWaveform = ({
  className = '',
  height = 80,
  barWidth = 2,
  barGap = 1,
  barColor = '#3b82f6',
  backgroundColor = 'rgba(0, 0, 0, 0.05)',
  silenceColor = '#CCCCCC',
}: AudioWaveformProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);
  const waveformHistoryRef = useRef<number[]>([]);
  const latestAudioDataRef = useRef<Uint8Array>(new Uint8Array());

  // Control bar insertion speed (matching Angular's throttling)
  const frameCounterRef = useRef<number>(0);
  const insertEveryNFrames = 3;

  const { audioProcessor } = useRecordingContext();
  const isRecording = useSelector(RecordingStateSelector.isRecording);
  const isPaused = useSelector(RecordingStateSelector.isPaused);

  // Audio data subscription state
  const [audioDataReady, setAudioDataReady] = useState(false);

  // Get isStopped from Redux store
  const isStopped = useSelector(RecordingStateSelector.isStopped);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctxRef.current = ctx;
    resizeCanvas();

    const handleResize = () => resizeCanvas();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      stopAnimation();
    };
  }, [height]);

  useEffect(() => {
    if (isRecording && !isPaused && !isStopped) {
      startAnimation();
    } else if (isRecording && isPaused && !isStopped) {
      stopAnimation();
    } else {
      // Clear waveform history when not recording, paused, or stopped
      waveformHistoryRef.current = [];
      frameCounterRef.current = 0;
      latestAudioDataRef.current = new Uint8Array();
      setAudioDataReady(false);
      stopAnimation();
    }

    return () => {
      stopAnimation();
    };
  }, [isRecording, isPaused, isStopped]);

  // Subscribe to frequency data from audioProcessor (matching Angular's waveformData$)
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    if (isRecording && audioProcessor) {
      unsubscribe = audioProcessor.onFrequencyDataChange((data) => {
        latestAudioDataRef.current = data;
        setAudioDataReady(true);
      });
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isRecording, audioProcessor]);

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.clientWidth;
      canvas.height = height;
    }
  };

  const startAnimation = () => {
    const animate = () => {
      drawSmoothWaveform();
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };
    animationFrameIdRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
  };

  const drawSmoothWaveform = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;

    const { width, height: canvasHeight } = canvas;
    const centerY = canvasHeight / 2;

    // Clear canvas
    ctx.clearRect(0, 0, width, canvasHeight);

    // Draw background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, canvasHeight);

    if (!isRecording || isPaused || isStopped) {
      // Draw flatline when not recording, paused, or stopped (matching Angular)
      ctx.fillStyle = silenceColor;
      ctx.fillRect(0, centerY - 1, width, 2);
      return;
    }

    // Calculate max bars based on canvas width (matching Angular)
    const step = barWidth + barGap;
    const maxBars = Math.floor(width / step);

    // Calculate volume from frequency data (matching Angular's RMS calculation)
    let volume = 0;
    if (latestAudioDataRef.current.length) {
      const rms = Math.sqrt(
        latestAudioDataRef.current.reduce((sum, val) => sum + val * val, 0) /
          latestAudioDataRef.current.length
      );
      volume = Math.min(rms / 128, 1);
    }

    const barHeight = volume * canvasHeight * 0.95;

    // Throttle bar insertion to slow down scrolling (matching Angular)
    frameCounterRef.current++;
    if (frameCounterRef.current % insertEveryNFrames === 0) {
      waveformHistoryRef.current.push(barHeight);
      if (waveformHistoryRef.current.length > maxBars) {
        waveformHistoryRef.current.shift();
      }
    }

    // Calculate offset for right-to-left scrolling (matching Angular)
    const offsetX = width - waveformHistoryRef.current.length * step;

    // Draw bars with rounded corners (matching Angular's quadraticCurveTo approach)
    for (let i = 0; i < waveformHistoryRef.current.length; i++) {
      const h = waveformHistoryRef.current[i];
      if (h <= 0) continue; // Skip empty bars

      const x = offsetX + i * step;
      const y = centerY - h / 2;
      const radius = 1;

      ctx.fillStyle = barColor;
      ctx.beginPath();

      // Top left corner
      ctx.moveTo(x + radius, y);
      // Top edge
      ctx.lineTo(x + barWidth - radius, y);
      // Top right corner
      ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
      // Right edge
      ctx.lineTo(x + barWidth, y + h - radius);
      // Bottom right corner
      ctx.quadraticCurveTo(x + barWidth, y + h, x + barWidth - radius, y + h);
      // Bottom edge
      ctx.lineTo(x + radius, y + h);
      // Bottom left corner
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      // Left edge
      ctx.lineTo(x, y + radius);
      // Back to top left corner
      ctx.quadraticCurveTo(x, y, x + radius, y);

      ctx.fill();
    }
  };

  const getStatusText = () => {
    if (isStopped) return 'Stopped';
    if (!isRecording) return 'Not Recording';
    if (isPaused) return 'Paused';
    if (!audioDataReady) return 'Initializing...';
    return 'Recording';
  };

  const getStatusColor = () => {
    if (isStopped) return 'bg-red-500';
    if (!isRecording) return 'bg-gray-500';
    if (isPaused) return 'bg-yellow-500';
    if (audioDataReady) return 'bg-green-500';
    return 'bg-blue-500';
  };

  return (
    <motion.div
      className={`relative overflow-hidden rounded-lg w-full ${className}`}
      style={{ height }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}>
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ display: 'block', width: '100%' }}
      />

      {/* Recording indicator */}
      {isRecording && !isPaused && !isStopped && audioDataReady && (
        <motion.div
          className={`absolute top-2 right-2 w-3 h-3 ${getStatusColor()} rounded-full`}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}

      {/* Paused indicator */}
      {isRecording && isPaused && !isStopped && (
        <div className="absolute top-2 right-2 w-3 h-3 bg-yellow-500 rounded-full" />
      )}

      {/* Stopped indicator */}
      {isStopped && (
        <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full" />
      )}
    </motion.div>
  );
};

export default AudioWaveform;
